import type { JSX } from "react";
import { Fragment } from "react";
import { Link } from "react-router-dom";

import {
  DEFAULT_RESOLVED_SECTIONS,
  resolvedGuideSections,
  SECTION_TYPES,
  sectionAnchor,
} from "@noko-city/shared";

import { AtlasCta } from "./AtlasCta";
import { SECTION_RENDERERS } from "./city-page/sections";
import { Footer } from "./Footer";
import { Grain } from "./Grain";
import type { GuideNavSection } from "./GuideNav";
import { GuideParallax } from "./GuideParallax";
import { GuideSection } from "./GuideSection";
import { ProgressBar } from "./ProgressBar";
import { Reveal } from "./Reveal";
import { StarField } from "./StarField";

import type { CityPage } from "@/lib/api";
import { readGuideCity, reloadGuideDocument } from "@/lib/guide-document";
import { toAsciiUpper } from "@/lib/time";

/** 'izmir' -> 'Izmir' — a readable city name from a URL slug for the error state. */
function nameFromSlug(slug: string): string {
  if (!slug) return "your city";
  return slug
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

/**
 * The delay every guide section's own entrance uses. It is applied from the
 * moment that section intersects, so a ladder that grows with document position
 * would make each lower section wait longer than the one above it for no reason
 * the reader can see.
 */
const SECTION_DELAY = 100;

/**
 * The guide's body: the payload's own composition, drawn.
 *
 * The list of sections is resolved data, not an assembly step. Order and
 * visibility arrive with the payload — one entry per section, in document order,
 * each saying whether it is drawn — so this component decides neither: it takes
 * the entries that are drawn, hands each to the renderer its type names, and
 * splits them into the hero's blocks (the registry's unnumbered ones, which open
 * the document) and the numbered sections that follow. A payload baked before the
 * field existed falls back to `resolvedGuideSections(DEFAULT_RESOLVED_SECTIONS,
 * city)`: the registry's own composition with the visibility rules the payload
 * builder applies, in shared, rather than a second copy of them here.
 *
 * The masthead, the closing CTA and the footer are chrome, not sections: they
 * close the document whether or not it has any sections at all.
 */
export function CityPageBody({ city }: { city: CityPage }): JSX.Element {
  const handle = city.instagramHandle;
  const sections = (city.sections ?? resolvedGuideSections(DEFAULT_RESOLVED_SECTIONS, city)).filter(
    (section) => section.rendered,
  );
  const numbered = sections.filter((section) => SECTION_TYPES[section.id].numbered);
  const hero = sections.filter((section) => !SECTION_TYPES[section.id].numbered);
  // The contents strip lists the numbered sections in the document's own order,
  // numbered exactly as their eyebrows are — the same list, so the strip cannot
  // name a section the reader cannot reach.
  const nav: GuideNavSection[] = numbered.map((section, position) => ({
    id: sectionAnchor(section.id),
    heading: section.heading,
    index: position + 1,
  }));

  return (
    <div className="landing-home atlas-guide relative min-h-screen">
      {/* Bypass, first in the DOM and hidden until focused: two links precede the
          guide's content. */}
      <a className="atlas-skip" href="#guide">
        SKIP TO THE GUIDE
      </a>

      {/* Near-black stage: the homepage's radial ink field plus the shared
          starfield, behind every layer of content. The post artwork is never
          overlaid or darkened — the cards carry their own ink. */}
      <div className="atlas-guide-stage" aria-hidden="true">
        <StarField />
      </div>
      <Grain />
      <ProgressBar />
      <GuideParallax />

      {/* Masthead — the homepage's wordmark and navigation, in flow rather than
          pinned over a globe. */}
      <nav className="atlas-masthead" aria-label="noko">
        <Link to="/" className="atlas-wordmark">
          noko
        </Link>
        <span className="atlas-tag">CITY EVENT GUIDE</span>
        <Link to="/" className="atlas-masthead-link">
          ALL CITIES
        </Link>
      </nav>

      <main className="atlas-guide-main" id="guide">
        {/* The hero: what the first screen holds — the identity, the post row and
            the contents strip, in the payload's own order among themselves. On
            desktop its `min-height` is one viewport less the masthead, which is
            the budget the post frames are sized against (home.css, ≥1024px); a
            viewport too short for that simply grows past one screen instead of
            clipping. */}
        <div className="atlas-guide-hero">
          {hero.map((section) => (
            <Fragment key={section.id}>
              {SECTION_RENDERERS[section.id]({ section, page: city, nav })}
            </Fragment>
          ))}
        </div>

        {numbered.map((section, position) => (
          <GuideSection
            key={section.id}
            id={sectionAnchor(section.id)}
            index={position + 1}
            heading={section.heading}
            icon={section.icon}
            // Alternating, assigned from the rendered position: a city that is
            // missing a section still gets two adjacent sections in two hues.
            tone={position % 2 === 0 ? "amber" : "magenta"}
            delay={SECTION_DELAY}
          >
            {SECTION_RENDERERS[section.id]({ section, page: city, nav })}
          </GuideSection>
        ))}

        {/* Closing CTA — repeat the Instagram follow over the homepage finale's
            orange glow, in ordinary flow, with the city's name in outline on the
            far side of the block: the page's own display line, at the scale the
            sections use for their numerals, so the ending is a composition
            rather than one line of type in an empty band. Decorative — the
            heading above already names the city for a screen reader. */}
        <Reveal variant="stamp" delay={120}>
          <section className="atlas-guide-col atlas-guide-closing">
            <p className="atlas-note">TONIGHT&apos;S FULL RUNDOWN IS ON INSTAGRAM.</p>
            {handle ? (
              <AtlasCta
                href={`https://instagram.com/${handle}`}
                label={`FOLLOW @${handle.toUpperCase()}`}
              />
            ) : (
              <AtlasCta to="/" label="BACK TO ALL CITIES" />
            )}
            <p className="atlas-guide-closing-city" aria-hidden="true">
              {toAsciiUpper(city.name)}
            </p>
          </section>
        </Reveal>
      </main>

      <div className="atlas-guide-footer">
        <Footer
          instagramHref={handle ? `https://instagram.com/${handle}` : undefined}
          instagramLabel={handle ? `@${handle.toUpperCase()}` : undefined}
        />
      </div>
    </div>
  );
}

/** The public per-city page. Never renders fake data — only the baked payload. */
export function CityPage({ slug }: { slug: string }): JSX.Element | null {
  const city = readGuideCity(slug);

  if (city) return <CityPageBody city={city} />;

  // No payload for this slug in this document: either there is no guide to render
  // (an unpublished or unknown slug) or an in-app link walked the SPA to a guide
  // URL. The handoff resolves the second case and only that one; see
  // lib/guide-document.ts. It reports nothing to render when it navigates.
  if (reloadGuideDocument()) return null;

  const display = nameFromSlug(slug);
  return (
    <div className="landing-home atlas-guide relative min-h-screen">
      <div className="atlas-guide-stage" aria-hidden="true">
        <StarField />
      </div>
      <Grain />
      <ProgressBar />
      <div className="atlas-guide-main atlas-guide-col flex min-h-screen flex-col items-start justify-center">
        <Reveal variant="rise">
          <h1 className="atlas-guide-city">{toAsciiUpper(display)}</h1>
        </Reveal>
        <Reveal variant="stamp" delay={120}>
          <p className="atlas-guide-lead">
            The {display} guide is on its way. Follow the handle on Instagram in the meantime.
          </p>
          <AtlasCta to="/" label="ALL CITIES" />
        </Reveal>
      </div>
    </div>
  );
}
